<!--suppress HtmlUnknownAttribute, HtmlWrongAttributeValue -->
<template>
    <div :class="`improvement-tile${this.hasSelectedAlternative ? ' improvement-tile--unused-alt' : ''}`">
        <div v-if="improvement.hasImage"
             class="improvement-tile__image"
             :style="`background-image: url('${this.improvement.imageUrl}');`"
             @click="onViewInfo()" />
        <div v-else
             class="improvement-tile__image --default"
             @click="onViewInfo()" />
        <section ref="tileData"
                 class="improvement-tile__data"
                 role="button"
                 tabindex="0"
                 @click="onViewInfo()"
                 @keyup.enter="onViewInfo()">
            <span v-if="category"
                  class="improvement-tile__data__category">{{ category }}</span>
            <h3 class="improvement-tile__data__title">
                {{ improvement.title }}
            </h3>
            <aside class="improvement-tile__data__subdata">
                <span class="improvement-tile__data__price"><span v-if="!scheme.priceless">{{ improvement.priceDisplay }}</span></span>
                <span v-if="displayMetric" class="improvement-tile__data__metrics">{{ displayMetric }}</span>
            </aside>
        </section>
        <section class="improvement-tile__actions">
            <div v-if="improvement.accepted"
                 role="button"
                 pressed="true"
                 :class="`improvement-tile__actions__select improvement-tile__actions__select--in-plan${this.lastChanged ? ' improvement-tile__actions__select--last-changed' : ''}`"
                 tabindex="0"
                 @click="onInPlanClick()"
                 @keyup.enter="onInPlanClick()">
                <span class="no-css">in plan</span>
            </div>
            <div v-else
                 role="button"
                 pressed="false"
                 class="improvement-tile__actions__select improvement-tile__actions__select--add"
                 tabindex="0"
                 @click="onAddClick()"
                 @keyup.enter="onAddClick()">
                + add
            </div>
            <div v-if="improvement.recommended"
                 class="improvement-tile__actions__recommended improvement-tile__actions__recommended--yes">
                <i class="fas fa-star" />
            </div>
            <div v-else
                 class="improvement-tile__actions__recommended improvement-tile__actions__recommended--no">
                <hh-tooltip text="This improvement isn't recommended to you but you can still add it if you like."
                            position="top-left"
                            top-bottom-position-adjust="35px"
                            right-left-position-adjust="-5px">
                    <i class="far fa-star" />
                </hh-tooltip>
            </div>
        </section>
        <div class="improvement-tile__mask"
             role="button"
             @click="onViewInfo()">
            <div class="improvement-tile__mask__info">
                <span class="improvement-tile__mask__info__text">conflicting measure selected</span>
            </div>
        </div>
    </div>
</template>

<script>

import Casing from '@/helpers/casing'
import PlanBuilderManager from '@/plan-builder-manager'

import Improvement from '@/models/improvement'

import AnalyticsLogger from '@/components/analytics-logger'
import SapDeltaFormatter from '@/components/sap-delta-formatter'
import ImprovementInfo from '@/components/app-core/modals/improvement-info'

import _ from 'lodash'

export default {

    props: {
        improvement: {
            type: Improvement,
            required: true,
        },
        lastChanged: {
            type: Boolean,
            required: true,
        },
        selectedImprovements: {
            type: Array,
            default: () => [],
        },
    },

    mixins: [
        AnalyticsLogger,
        SapDeltaFormatter,
    ],

    computed: {

        /**
         * @returns {?string}
         */
        category() {
            const mapping = this.scheme.segmentKeymap.find(map => map.key === this.improvement.segmentKey)
            return mapping ? mapping.text : null
        },

        /**
         * @returns {string}
         */
        displayMetric() {
            return this.improvement.savings
                ? this.scheme.showSapMetrics
                    ? this.formatSapChangeText(this.improvement.savings.EPCScore, true)
                    : this.improvement.savings.carbonDioxide
                : ''
        },

        /**
         * @returns {boolean}
         */
        hasSelectedAlternative() {
            return this.improvement.hasSelectedAlternativeIn(this.selectedImprovements)
        },

        /**
         * @returns {Scheme}
         */
        scheme() {
            return this.$scheme
        },
    },

    methods: {

        async onAddClick() {
            this.$emit('change', this.improvement)
            const retVal = await PlanBuilderManager.addImprovementToProjectAsync(this.improvement)
            this.trackEvent('plan_build', 'add_measure_to_plan', { additional: this.improvement.title })
            this.trackEvent('plan_build', 'measure_added_to_plan', { value: this.improvement.title })
            return retVal
        },

        async onInPlanClick() {
            this.$emit('change', this.improvement)
            await PlanBuilderManager.removeImprovementFromProjectAsync(this.improvement)
            this.trackEvent('plan_build', 'remove_measure_from_plan', { additional: this.improvement.title })
            this.trackEvent('plan_build', 'measure_removed_from_plan', { value: this.improvement.title })
        },

        onViewInfo() {
            const key = Casing.kebabCase(this.improvement.title)
            history.pushState({ key }, this.improvement.title, `#${ key }`)
            this.$emit('viewed', this.$refs.tileData)
            this.$eventbus.$emit(this.$appevents.showModal, ImprovementInfo, {
                data: { item: _.cloneDeep(this.improvement) },
                cssModifier: 'info',
                onClose: () => {
                    history.back()
                },
            })
            this.trackEvent('plan_build', 'view_measure', { additional: this.improvement.title })
            this.trackEvent('plan_build', 'measure_viewed', { value: this.improvement.title })
        },
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\components\plan-builder\_improvement-tile.scss

$tile-data-padding: rem(5px);
$tile-actions-area-height-px: 34px;
$tile-actions-area-height: rem($tile-actions-area-height-px);

.improvement-tile {
    height: 100%;
    position: relative;
    padding-bottom: $tile-actions-area-height;
    @include user-select-none;

    @include element('image') {
        width: 100%;
        background-position: center;
        background-repeat: no-repeat;
        background-size: cover;
        height: 6rem;
        cursor: pointer;

        @include bp-min-small {
            height: 7rem;
        }
    }

    @include element('data') {
        font-size: 0.8rem;
        padding: $tile-data-padding;
        min-height: rem(100px);
        cursor: pointer;

        @include bp-min-small {
            height: 0.9rem;
            min-height: rem(120px);
        }

        > * {
            margin: 0;
            display: block;
        }

        @include element('title') {
            font-size: 0.9rem;
            margin-bottom: rem(15px);

            @include bp-min-small {
                font-size: 1rem;
                margin-bottom: rem(20px);
            }
        }

        @include element('subdata') {
            position: absolute;
            bottom: $tile-actions-area-height;
            left: $tile-data-padding;
            right: $tile-data-padding;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
        }
    }

    @include element('actions') {
        height: $tile-actions-area-height;
        display: table;
        width: 100%;
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;

        > * {
            display: table-cell;
            text-align: center;
            vertical-align: middle;
        }

        @include element('select') {
            cursor: pointer;
            font-weight: bold;
            font-size: 0.9rem;
            //noinspection CssOverwrittenProperties - Fallback
            width: 75%;
            //noinspection CssOverwrittenProperties
            width: rem(calc(100% - #{$tile-actions-area-height-px}));

            @include bp-min-small {
                font-size: 1rem;
            }

            @include modifier('in-plan') {

                &:after {
                    content: 'in plan'
                }

                @media (hover: hover) {
                    &:hover {
                        &:after {
                            content: '- remove'
                        }
                    }
                }
            }

            @include modifier('last-changed') {

                &:after {
                    content: 'in plan' !important
                }
            }
        }

        @include element('recommended') {
            font-size: 1.1rem;
            width: $tile-actions-area-height;

            @include bp-min-small {
                font-size: 1.3rem;
            }
        }
    }

    @include element('mask') {
        position: absolute;
        z-index: z-index('mask');
        top: 0; bottom: $tile-actions-area-height; left: 0; right: 0;
        opacity: 0.75;
        cursor: pointer;
        display: none;

        @include element('info') {
            text-align: center;
            padding: 1rem;
            font-weight: bold;
            margin-top: 1rem;

            @include element('text') {
                font-size: smaller;
            }
        }
    }

    @include modifier('unused-alt') {

        > .improvement-tile__mask {
            display: block;
        }
    }
}

</style>
